import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import type { FormSubmissionDetailDto, FormSubmissionDto } from "src/http";
import {
  FetchError,
  optimisticallyUpdateQueriesData,
  optimisticallyUpdateQueryData,
  queryKeys,
  useAccessToken,
} from "src/http";

type Variables = {
  formSubmissionGuid: string;
};

const useReviewFormSubmissionInOneOnOne = () => {
  const { getAccessToken } = useAccessToken();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  async function reviewFormSubmissionInOneOnOneRequest(variables: Variables) {
    const accessToken = await getAccessToken();

    const response = await fetch(
      `${configuration.apiRootUrl}/form-submissions/${variables.formSubmissionGuid}/one-on-one-review`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        method: "post",
      },
    );

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return useMutation({
    mutationFn: reviewFormSubmissionInOneOnOneRequest,
    onMutate: async (variables) => {
      const cachedFormSubmissionData = optimisticallyUpdateQueryData<FormSubmissionDetailDto>(
        queryClient,
        queryKeys.formSubmissions.getFormSubmission(variables.formSubmissionGuid),
        (cachedData) => ({
          ...cachedData,
          reviewedInOneOnOne: true,
        }),
      );

      const cachedFormSubmissionsData = await optimisticallyUpdateQueriesData<FormSubmissionDto[]>(
        queryClient,
        queryKeys.formSubmissions.getFormSubmissions(),
        (cachedData) =>
          cachedData.map((formSubmission) => {
            if (formSubmission.formSubmissionGuid === variables.formSubmissionGuid) {
              return {
                ...formSubmission,
                reviewedInOneOnOne: true,
              };
            } else {
              return formSubmission;
            }
          }),
        (cachedData) =>
          cachedData.some((formSubmission) => formSubmission.formSubmissionGuid === variables.formSubmissionGuid),
      );

      return { cachedFormSubmissionData, cachedFormSubmissionsData };
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.formSubmissions.getFormSubmission(variables.formSubmissionGuid),
        context?.cachedFormSubmissionData,
      );

      if (context?.cachedFormSubmissionsData) {
        context.cachedFormSubmissionsData.forEach((data, key) => queryClient.setQueryData(key, data));
      }
    },
    onSuccess: (_data, variables) => {
      signalR.sendUpdateFormSubmissions(variables.formSubmissionGuid, {
        reviewedInOneOnOne: true,
      });

      signalR.sendUpdateFormSubmissionDetail(variables.formSubmissionGuid, {
        reviewedInOneOnOne: true,
      });
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.formSubmissions.getFormSubmission(variables.formSubmissionGuid));
      queryClient.invalidateQueries(queryKeys.formSubmissions.getFormSubmissions());
    },
  });
};

export { useReviewFormSubmissionInOneOnOne };
